/**
 * Client Inventory Picker Dialog
 *
 * Shows client's actual inventory (like InventoryPage) for product selection
 * Users can see location-specific stock and select products directly
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Card } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Alert, AlertDescription } from '../../ui/alert';
import { Checkbox } from '../../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../ui/tooltip';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../ui/collapsible';
import {
  X,
  Search,
  ChevronDown,
  ChevronUp,
  Plus,
  Minus,
  Loader2,
  History
} from 'lucide-react';
import inventoryService from '../../../services/inventoryService';
import InventoryHistoryDialog from '../../InventoryHistoryDialog';

function ClientInventoryPickerDialog({ open, onClose, clientId, clientName, onConfirm }) {
  const [loading, setLoading] = useState(false);
  const [inventory, setInventory] = useState([]);
  const [filteredInventory, setFilteredInventory] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [locationFilter, setLocationFilter] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState({}); // { product_code: { total, locations: [{location, quantity}] } }
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [error, setError] = useState(null);
  const [historyDialog, setHistoryDialog] = useState({ open: false, product: null });

  // Load client inventory on open
  const loadClientInventory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Use global inventory view (cross-client stock is allowed)
      const response = await inventoryService.getInventory(1, 1000, 'name', 'asc', {}, null);

      // Group inventory by product code, tracking each location+source_client separately
      const grouped = {};
      response.inventory?.forEach(item => {
        const code = item.product_code || item.product_id;
        if (!grouped[code]) {
          grouped[code] = {
            product_code: code,
            product_name: item.product_name || item.name,
            specification: item.specification,
            unit: item.unit,
            category: item.category,
            locations: [],
            locationMap: {}, // Track by location+source_client_id
            totalAvailable: 0
          };
        }

        const locationName = item.location || 'main';
        const sourceClientId = item.source_client_id || item.client_id || 'unknown';
        const locationKey = `${locationName}|${sourceClientId}`;
        // Use remaining_quantity from /api/inventory, fallback to available_quantity
        const qty = item.remaining_quantity || item.available_quantity || 0;

        // Check if this location+source_client combo already exists
        if (grouped[code].locationMap[locationKey]) {
          // Merge: add quantity to existing entry
          grouped[code].locationMap[locationKey].available_quantity += qty;
          // Keep the most recent data_freshness and last_movement
          if (item.last_movement?.created_at > grouped[code].locationMap[locationKey].last_movement?.created_at) {
            grouped[code].locationMap[locationKey].data_freshness = item.data_freshness;
            grouped[code].locationMap[locationKey].last_movement = item.last_movement;
          }
        } else {
          // New location+source_client entry
          grouped[code].locationMap[locationKey] = {
            location: locationName,
            source_client_id: sourceClientId,
            source_client_name: item.source_client_name || item.client_name || '未知客户',
            available_quantity: qty,
            data_freshness: item.data_freshness,
            last_movement: item.last_movement
          };
        }

        grouped[code].totalAvailable += qty;
      });

      // Convert locationMap to locations array
      Object.values(grouped).forEach(product => {
        product.locations = Object.values(product.locationMap);
        delete product.locationMap; // Clean up temporary map
      });

      const inventoryArray = Object.values(grouped).filter(p => p.totalAvailable > 0);
      setInventory(inventoryArray);
      setFilteredInventory(inventoryArray);
    } catch (err) {
      setError(err.response?.data?.error || '加载库存失败');
      console.error('Error loading client inventory:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open && clientId) {
      loadClientInventory();
    }
  }, [open, clientId, loadClientInventory]);

  // Filter inventory based on search and location
  useEffect(() => {
    let filtered = inventory;

    // Search filter
    if (searchText.trim()) {
      const search = searchText.toLowerCase();
      filtered = filtered.filter(p =>
        p.product_code?.toLowerCase().includes(search) ||
        p.product_name?.toLowerCase().includes(search) ||
        p.specification?.toLowerCase().includes(search)
      );
    }

    // Location filter
    if (locationFilter && locationFilter !== 'all') {
      filtered = filtered.filter(p =>
        p.locations.some(loc => loc.location === locationFilter)
      );
    }

    setFilteredInventory(filtered);
  }, [searchText, locationFilter, inventory]);

  // Get unique locations for filter
  const availableLocations = [...new Set(
    inventory.flatMap(p => p.locations.map(loc => loc.location))
  )].sort();

  // Toggle row expansion
  const toggleRow = (productCode) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(productCode)) {
      newExpanded.delete(productCode);
    } else {
      newExpanded.add(productCode);
    }
    setExpandedRows(newExpanded);
  };

  // Handle product selection
  const handleProductSelect = (product, checked) => {
    const newSelected = { ...selectedProducts };

    if (checked) {
      // Auto-expand when selected
      setExpandedRows(prev => new Set([...prev, product.product_code]));

      newSelected[product.product_code] = {
        product_code: product.product_code,
        product_name: product.product_name,
        specification: product.specification,
        unit: product.unit,
        totalAvailable: product.totalAvailable,
        locations: product.locations,
        totalSelected: 0,
        locationAllocations: []
      };
    } else {
      delete newSelected[product.product_code];
    }

    setSelectedProducts(newSelected);
  };

  // Handle quantity change for specific location
  // locKey format: "location|source_client_id" to uniquely identify stock source
  const handleLocationQuantityChange = (productCode, locKey, quantity) => {
    const newSelected = { ...selectedProducts };
    if (!newSelected[productCode]) return;

    // Parse locKey to get location and source_client_id
    const [location, sourceClientId] = locKey.split('|');

    const qty = parseFloat(quantity) || 0;
    // Find location data matching both location AND source_client_id
    const locationData = newSelected[productCode].locations.find(l =>
      l.location === location && (l.source_client_id || 'unknown') === sourceClientId
    );

    if (!locationData) return;

    // Validate against available
    if (qty > locationData.available_quantity) {
      return; // Don't allow over-allocation
    }

    // Update or add location allocation (now keyed by locKey)
    const allocations = newSelected[productCode].locationAllocations || [];
    const existingIndex = allocations.findIndex(a => a.locKey === locKey);

    if (qty > 0) {
      if (existingIndex >= 0) {
        allocations[existingIndex].quantity = qty;
      } else {
        // Include source_client_id in allocation for backend transfer logic
        allocations.push({
          locKey,
          location,
          source_client_id: sourceClientId !== 'unknown' ? sourceClientId : null,
          source_client_name: locationData.source_client_name,
          quantity: qty
        });
      }
    } else {
      // Remove if quantity is 0
      if (existingIndex >= 0) {
        allocations.splice(existingIndex, 1);
      }
    }

    newSelected[productCode].locationAllocations = allocations;
    newSelected[productCode].totalSelected = allocations.reduce((sum, a) => sum + a.quantity, 0);

    setSelectedProducts(newSelected);
  };

  // Get selected quantity for a location (using locKey: location|source_client_id)
  const getLocationQuantity = (productCode, locKey) => {
    const product = selectedProducts[productCode];
    if (!product) return 0;
    const alloc = product.locationAllocations?.find(a => a.locKey === locKey);
    return alloc?.quantity || 0;
  };

  // Handle confirm
  const handleConfirm = () => {
    const productsToAdd = Object.values(selectedProducts)
      .filter(p => p.totalSelected > 0)
      .map(p => ({
        product_id: p.product_code, // Required for backend - use product_code as product_id
        product_code: p.product_code,
        name: p.product_name,
        product_name: p.product_name,
        specification: p.specification,
        unit: p.unit,
        quantity_planned: p.totalSelected,
        quantity_actual: p.totalSelected,
        quantity: p.totalSelected,
        locations: p.locations,
        locationAllocations: p.locationAllocations,
        totalAvailable: p.totalAvailable,
        available_quantity: p.totalAvailable
      }));

    onConfirm(productsToAdd);
    handleClose();
  };

  const handleClose = () => {
    setSearchText('');
    setLocationFilter('');
    setSelectedProducts({});
    setExpandedRows(new Set());
    onClose();
  };

  const selectedCount = Object.values(selectedProducts).filter(p => p.totalSelected > 0).length;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl h-[90vh] flex flex-col">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle>选择产品 - 客户库存</DialogTitle>
              <p className="text-sm text-muted-foreground mt-1">{clientName}</p>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Search and Filters */}
          <div className="flex gap-2 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="搜索产品编码、名称、规格..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                className="pl-10"
              />
            </div>

            <Select value={locationFilter} onValueChange={setLocationFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="库位筛选" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">全部库位</SelectItem>
                {availableLocations.map(loc => (
                  <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Badge variant={selectedCount > 0 ? 'default' : 'outline'} className="flex items-center px-3">
              已选: {selectedCount}
            </Badge>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : (
            <Card className="flex-1 overflow-auto border">
              <table className="w-full text-sm">
                <thead className="bg-muted sticky top-0">
                  <tr className="border-b">
                    <th className="p-3 w-12"></th>
                    <th className="text-left p-3 font-medium">产品编码</th>
                    <th className="text-left p-3 font-medium">规格</th>
                    <th className="text-right p-3 font-medium">总库存</th>
                    <th className="text-right p-3 font-medium">已选数量</th>
                    <th className="p-3 w-20 font-medium">库位明细</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInventory.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center p-8">
                        <span className="text-sm text-muted-foreground">暂无库存数据</span>
                      </td>
                    </tr>
                  ) : (
                    filteredInventory.map((product, productIndex) => {
                      const isSelected = !!selectedProducts[product.product_code];
                      const isExpanded = expandedRows.has(product.product_code);
                      const totalSelected = selectedProducts[product.product_code]?.totalSelected || 0;
                      // Generate unique key using product_code + specification + index
                      const productKey = `${product.product_code}_${product.specification || ''}_${productIndex}`;

                      return (
                        <Collapsible
                          key={productKey}
                          open={isExpanded}
                          onOpenChange={() => toggleRow(product.product_code)}
                          asChild
                        >
                          <React.Fragment>
                            {/* Main Row */}
                            <tr
                              className={`border-b hover:bg-muted cursor-pointer ${isSelected ? 'bg-primary/10' : ''}`}
                            >
                              <td className="p-3">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={(checked) => handleProductSelect(product, checked)}
                                />
                              </td>
                              <td className="p-3">
                                <div>
                                  <span className="font-medium">{product.product_code}</span>
                                  <span className="block text-xs text-muted-foreground">{product.product_name}</span>
                                </div>
                              </td>
                              <td className="p-3">
                                <span className="text-sm">{product.specification || '-'}</span>
                              </td>
                              <td className="p-3 text-right">
                                <Badge variant={product.totalAvailable > 0 ? 'default' : 'outline'}>
                                  {product.totalAvailable} {product.unit || '支'}
                                </Badge>
                              </td>
                              <td className="p-3 text-right">
                                {isSelected && totalSelected > 0 && (
                                  <Badge variant="default">
                                    {totalSelected} {product.unit || '支'}
                                  </Badge>
                                )}
                              </td>
                              <td className="p-3">
                                <CollapsibleTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                                  </Button>
                                </CollapsibleTrigger>
                              </td>
                            </tr>

                            {/* Location Details */}
                            <tr>
                              <td colSpan={6} className="p-0">
                                <CollapsibleContent>
                                  <div className="p-4 bg-muted">
                                    <span className="text-xs text-muted-foreground block mb-2">库位明细：</span>
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b">
                                          <th className="text-left p-2 font-medium">库位</th>
                                          <th className="text-left p-2 font-medium">来源客户</th>
                                          <th className="text-right p-2 font-medium">可用数量</th>
                                          <th className="text-center p-2 font-medium w-16">历史</th>
                                          <th className="text-right p-2 font-medium">选择数量</th>
                                          <th className="p-2 w-24"></th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {product.locations.map((loc, locIndex) => {
                                          // Use location + source_client_id as unique key
                                          const locKey = `${loc.location}|${loc.source_client_id || 'unknown'}`;
                                          const selectedQty = getLocationQuantity(product.product_code, locKey);
                                          const maxQty = loc.available_quantity;
                                          const locationKey = `${product.product_code}_${locKey}_${locIndex}`;

                                          return (
                                            <tr key={locationKey} className="border-b">
                                              <td className="p-2">
                                                <span className="font-medium">{loc.location}</span>
                                              </td>
                                              <td className="p-2">
                                                <span className="text-muted-foreground">{loc.source_client_name || '未知'}</span>
                                              </td>
                                              <td className="p-2 text-right">
                                                <span>{loc.available_quantity} {product.unit || '支'}</span>
                                              </td>
                                              <td className="p-2 text-center">
                                                <TooltipProvider>
                                                  <Tooltip>
                                                    <TooltipTrigger asChild>
                                                      <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-7 w-7"
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          setHistoryDialog({
                                                            open: true,
                                                            product: {
                                                              _id: product.product_code,
                                                              product_id: product.product_code,
                                                              product_code: product.product_code,
                                                              name: product.product_name,
                                                              specification: product.specification,
                                                              unit: product.unit,
                                                              location: loc.location
                                                            }
                                                          });
                                                        }}
                                                      >
                                                        <History className="h-4 w-4" />
                                                      </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>查看库存历史</TooltipContent>
                                                  </Tooltip>
                                                </TooltipProvider>
                                              </td>
                                              <td className="p-2 text-right">
                                                <Input
                                                  type="number"
                                                  value={selectedQty || ''}
                                                  onChange={(e) => handleLocationQuantityChange(
                                                    product.product_code,
                                                    locKey,
                                                    e.target.value
                                                  )}
                                                  disabled={!isSelected}
                                                  className={`w-24 text-right ${selectedQty > maxQty ? 'border-red-500' : ''}`}
                                                  min={0}
                                                  max={maxQty}
                                                  step={1}
                                                />
                                                {selectedQty > maxQty && (
                                                  <span className="text-xs text-destructive">超出库存</span>
                                                )}
                                              </td>
                                              <td className="p-2">
                                                <div className="flex gap-1">
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    disabled={!isSelected || selectedQty >= maxQty}
                                                    onClick={() => handleLocationQuantityChange(
                                                      product.product_code,
                                                      locKey,
                                                      Math.min(selectedQty + 10, maxQty)
                                                    )}
                                                  >
                                                    <Plus className="h-4 w-4" />
                                                  </Button>
                                                  <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-8 w-8"
                                                    disabled={!isSelected || selectedQty <= 0}
                                                    onClick={() => handleLocationQuantityChange(
                                                      product.product_code,
                                                      locKey,
                                                      Math.max(selectedQty - 10, 0)
                                                    )}
                                                  >
                                                    <Minus className="h-4 w-4" />
                                                  </Button>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </CollapsibleContent>
                              </td>
                            </tr>
                          </React.Fragment>
                        </Collapsible>
                      );
                    })
                  )}
                </tbody>
              </table>
            </Card>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            取消
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selectedCount === 0}
          >
            确认添加 ({selectedCount}个产品)
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Inventory History Dialog */}
      <InventoryHistoryDialog
        open={historyDialog.open}
        onClose={() => setHistoryDialog({ open: false, product: null })}
        productId={historyDialog.product?.product_id || historyDialog.product?.product_code}
        productInfo={historyDialog.product}
      />
    </Dialog>
  );
}

export default ClientInventoryPickerDialog;
